import React from 'react';
import styled from 'styled-components';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'gatsby';
import { device } from '../utils/breakpoints';

const ResourcesWrapper = styled.section`
  background: #F9FAFB;
  padding: 100px 0;
  
  @media ${device.tablet} {
    padding: 60px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 16px;
  
  @media ${device.tablet} {
    font-size: 32px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: #000000;
  max-width: 700px;
  margin: 0 auto 20px;
`;

const ResourcesHubLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563eb;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;

  &:hover {
    color: #1d4ed8;
    text-decoration: none;
  }

  &::after {
    content: '→';
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(4px);
  }
`;

const ResourceCol = styled(Col)`
  margin-bottom: 24px;
  
  @media (min-width: 992px) {
    margin-bottom: 0;
  }
`;

const ResourceCard = styled(Link)`
  display: block;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 1px solid #E5E7EB;
  text-decoration: none;
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    text-decoration: none;
  }
`;

const ResourceImage = styled.div`
  background: linear-gradient(135deg, ${props => props.color1} 0%, ${props => props.color2} 100%);
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 64px;
`;

const ResourceContent = styled.div`
  padding: 30px;
`;

const ResourceCategory = styled.div`
  display: inline-block;
  background: #EBF5FF;
  color: #2563EB;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const ResourceTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 12px;
`;

const ResourceDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #000000;
  margin: 0;
`;

const ViewAllLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #2563EB;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  margin-top: 40px;
  
  &:hover {
    color: #1D4ED8;
    text-decoration: none;
  }
  
  &::after {
    content: '→';
    transition: transform 0.3s ease;
  }
  
  &:hover::after {
    transform: translateX(4px);
  }
`;

const ResourcesSection = () => {
  const resources = [
    {
      category: 'Documentation',
      title: 'Getting Started Guide',
      description: 'Learn how to deploy your first application on Ametnes Cloud in minutes.',
      icon: '📚',
      color1: '#EBF5FF',
      color2: '#DBEAFE',
      link: '/docs/'
    },
    {
      category: 'Case Study',
      title: 'Education Company Implements Private AI Solution',
      description: 'How an education company deployed self-hosted open-source LLM solutions using the Ametnes platform on private NVIDIA GPUs.',
      icon: '🎓',
      color1: '#F0FDF4',
      color2: '#DCFCE7',
      link: '/case-studies'
    },
    {
      category: 'Case Study',
      title: 'Communications Company Expands into Regulated Market',
      description: 'Large communications company overcame data sovereignty challenges by deploying self-hosted open-source solutions using the Ametnes platform.',
      icon: '📡',
      color1: '#FEF3C7',
      color2: '#FDE68A',
      link: '/case-studies'
    }
  ];

  return (
    <ResourcesWrapper>
      <Container>
        <SectionHeader>
          <SectionTitle>Resources & Learning</SectionTitle>
          <SectionSubtitle>
            Real success stories from organizations that transformed their infrastructure with Ametnes
          </SectionSubtitle>
          <div style={{ textAlign: 'center' }}>
            <ResourcesHubLink to="/blog">Browse the blog</ResourcesHubLink>
            {' · '}
            <ResourcesHubLink to="/resources">Browse documentation, architectures, and blogs</ResourcesHubLink>
          </div>
        </SectionHeader>
        
        <Row>
          {resources.map((resource, index) => (
            <ResourceCol lg={4} md={6} key={index}>
              <ResourceCard to={resource.link}>
                <ResourceImage color1={resource.color1} color2={resource.color2}>
                  {resource.icon}
                </ResourceImage>
                <ResourceContent>
                  <ResourceCategory>{resource.category}</ResourceCategory>
                  <ResourceTitle>{resource.title}</ResourceTitle>
                  <ResourceDescription>{resource.description}</ResourceDescription>
                </ResourceContent>
              </ResourceCard>
            </ResourceCol>
          ))}
        </Row>
        
        <div style={{ textAlign: 'center' }}>
          <ViewAllLink to="/case-studies">
            View All Case Studies
          </ViewAllLink>
        </div>
      </Container>
    </ResourcesWrapper>
  );
};

export default ResourcesSection;


